// 차원 (design 02 3.2): dimensions people make, next to the built-in 사람 ·
// 분류 · 결제수단 in 항목 관리. A dimension is a list of items (범주: the
// order is for showing; 순서: shift, cumsum and diff go along it) or a
// range of the whole numbers 1..N (순서). Its name is what formulas use:
// =sum(내텐서, over=정기), 내텐서[회차=3].
import { type FormEvent, useState } from 'react'

import { errorText } from '../../api/client'
import { ErrorText, Field, Sheet } from '../../ui/common'
import { showToast } from '../../ui/toast'
import type { Id, TensorDim } from '../api'
import { NAME_MAX } from '../csv'
import { customDims, dimItems, dimKindText, identProblem } from '../dims'
import { engine } from '../engine'
import type { Current } from './common'
import { ItemRow, ItemSheet } from './Items'

/** Names a new dimension must not take (the server checks them too). */
function takenNames(cur: Current, except: Id = 0): string[] {
  return customDims(cur.book?.dims)
    .filter((d) => d.id !== except)
    .map((d) => d.name)
}

type Shape = 'list' | 'ordered' | 'range'

export function NewDimSheet({ cur, open, onClose, onMade }: { cur: Current; open: boolean; onClose: () => void; onMade: (id: Id) => void }) {
  const [name, setName] = useState('')
  const [shape, setShape] = useState<Shape>('list')
  const [size, setSize] = useState('12')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const why = identProblem(name, takenNames(cur))
    const n = Number(size)
    if (why) return setError(why)
    if (shape === 'range' && !(Number.isInteger(n) && n >= 1 && n <= 1000)) return setError('크기는 1~1000입니다.')
    setBusy(true)
    setError(null)
    try {
      const d = await engine.addDim(cur.workbook.id, {
        name: name.trim(),
        kind: shape === 'list' ? 'category' : 'ordered',
        ...(shape === 'range' ? { size: n } : {}),
      })
      setName('')
      onMade(d.id)
      onClose()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="새 차원">
      <form className="t-form" onSubmit={(e) => void submit(e)}>
        <Field label="이름" hint="수식에서 쓰는 이름입니다. 예: 정기, 회차">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} autoComplete="off" />
        </Field>
        <fieldset className="t-dim-shapes">
          <legend>종류</legend>
          <label className="radio-choice">
            <input type="radio" name="dim-shape" checked={shape === 'list'} onChange={() => setShape('list')} /> 범주 (항목 목록, 순서는 보기용)
          </label>
          <label className="radio-choice">
            <input type="radio" name="dim-shape" checked={shape === 'ordered'} onChange={() => setShape('ordered')} /> 순서 (순서 있는 항목)
          </label>
          <label className="radio-choice">
            <input type="radio" name="dim-shape" checked={shape === 'range'} onChange={() => setShape('range')} /> 순서 (정수 1..N)
          </label>
        </fieldset>
        {shape === 'range' && (
          <Field label="N (1~1000)">
            <input inputMode="numeric" value={size} onChange={(e) => setSize(e.target.value.replace(/[^0-9]/g, ''))} />
          </Field>
        )}
        <p className="muted small">순서 차원은 shift·cumsum·diff(전월 대비, 누계, 차이)에 쓸 수 있습니다.</p>
        <ErrorText text={error} />
        <button type="submit" className="primary" disabled={busy || name.trim() === ''}>
          만들기
        </button>
      </form>
    </Sheet>
  )
}

function DimSettings({ cur, dim }: { cur: Current; dim: TensorDim }) {
  const [name, setName] = useState(dim.name)
  const [ordered, setOrdered] = useState(dim.kind === 'ordered')
  const [size, setSize] = useState(String(dim.size ?? ''))
  const [error, setError] = useState<string | null>(null)

  async function save(e: FormEvent) {
    e.preventDefault()
    const body: { name?: string; kind?: TensorDim['kind']; size?: number; version: number } = { version: dim.version }
    if (name.trim() !== dim.name) {
      const why = identProblem(name, takenNames(cur, dim.id))
      if (why) return setError(why)
      body.name = name.trim()
    }
    if (dim.size === null && ordered !== (dim.kind === 'ordered')) body.kind = ordered ? 'ordered' : 'category'
    if (dim.size !== null) {
      const n = Number(size)
      if (!(Number.isInteger(n) && n >= 1 && n <= 1000)) return setError('크기는 1~1000입니다.')
      if (n !== dim.size) body.size = n
    }
    if (body.name === undefined && body.kind === undefined && body.size === undefined) return
    setError(null)
    try {
      await engine.updateDim(cur.workbook.id, dim.id, body)
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function remove() {
    if (!confirm(`차원 '${dim.name}'을(를) 지울까요? 이 차원을 쓰던 수식은 오류가 됩니다. 이 차원 위에 만든 텐서가 있으면 먼저 지워야 합니다.`)) return
    try {
      await engine.deleteDim(cur.workbook.id, dim.id)
    } catch (err) {
      showToast(errorText(err))
    }
  }

  return (
    <form className="t-form" onSubmit={(e) => void save(e)}>
      <Field label="차원 이름" hint="이름을 바꿔도 수식은 저절로 고쳐지지 않습니다.">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} />
      </Field>
      {dim.size === null ? (
        <label className="radio-choice">
          <input type="checkbox" checked={ordered} onChange={(e) => setOrdered(e.target.checked)} /> 순서 차원 (shift·cumsum·diff에 쓴다)
        </label>
      ) : (
        <Field label="정수 1..N의 N" hint="줄이면 넘는 칸은 보이지 않게 되고, 다시 늘리면 돌아옵니다.">
          <input inputMode="numeric" value={size} onChange={(e) => setSize(e.target.value.replace(/[^0-9]/g, ''))} />
        </Field>
      )}
      <ErrorText text={error} />
      <div className="t-buttons">
        <button type="button" className="danger-btn" onClick={() => void remove()}>
          차원 지우기
        </button>
        <button type="submit" className="primary grow">
          저장
        </button>
      </div>
    </form>
  )
}

function AddDimItem({ cur, dim }: { cur: Current; dim: TensorDim }) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  async function submit(e: FormEvent) {
    e.preventDefault()
    const n = name.trim().normalize('NFC')
    if (n === '' || Array.from(n).length > NAME_MAX || n.includes('>')) {
      setError(`이름은 1~${NAME_MAX}자이고 '>'를 쓸 수 없습니다.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await engine.addDimItem(cur.workbook.id, dim.id, n)
      setName('')
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <form className="t-add-item" onSubmit={(e) => void submit(e)}>
      <Field label="새 항목 이름">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} />
      </Field>
      <ErrorText text={error} />
      <button className="secondary" type="submit" disabled={busy || name.trim() === ''}>
        더하기
      </button>
    </form>
  )
}

/** One dimension people made: its settings and its items. */
export function DimPanel({ cur, dim }: { cur: Current; dim: TensorDim }) {
  const [editing, setEditing] = useState<Parameters<typeof ItemSheet>[0]['item']>(null)
  const list = dimItems(cur.book?.items ?? {}, dim.id)
  return (
    <>
      <section className="card">
        <p className="muted small">
          {dimKindText(dim)} · 수식에서 <code>{dim.name}</code>
        </p>
        <DimSettings key={`${dim.id}:${dim.version}`} cur={cur} dim={dim} />
      </section>
      {dim.size === null ? (
        <>
          <section className="card">
            <AddDimItem cur={cur} dim={dim} />
          </section>
          <section className="card">
            <ul className="list t-items">
              {list.map((it, i) => (
                <ItemRow key={it.id} cur={cur} item={it} index={i} count={list.length} onEdit={setEditing} />
              ))}
            </ul>
            {list.length === 0 && <p className="muted">항목이 없습니다.</p>}
          </section>
        </>
      ) : (
        <p className="muted empty-note">
          항목은 1부터 {dim.size}까지의 정수입니다.
        </p>
      )}
      <ItemSheet cur={cur} item={editing} onClose={() => setEditing(null)} />
    </>
  )
}
